import { AVATAR_COLORS } from "./constants.js";
/** Hash estável e barato, usado para escolher cor de avatar a partir do id. */
export function stableHash(input) {
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
export function avatarColor(userId) {
    return AVATAR_COLORS[stableHash(userId) % AVATAR_COLORS.length];
}
export function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    const first = parts[0];
    if (parts.length === 1)
        return first.slice(0, 2).toUpperCase();
    const last = parts[parts.length - 1];
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
}
export function formatBytes(bytes, decimals = 1) {
    if (!Number.isFinite(bytes) || bytes <= 0)
        return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** i;
    return `${value.toFixed(i === 0 ? 0 : decimals)} ${units[i]}`;
}
export function formatBitrate(bps) {
    if (!Number.isFinite(bps) || bps <= 0)
        return '0 bps';
    const units = ['bps', 'kbps', 'Mbps', 'Gbps'];
    const i = Math.min(Math.floor(Math.log(bps) / Math.log(1000)), units.length - 1);
    const value = bps / 1000 ** i;
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
export function formatDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0)
        return '0s';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (d > 0)
        return `${d}d ${h}h`;
    if (h > 0)
        return `${h}h ${m}min`;
    if (m > 0)
        return `${m}min ${s}s`;
    return `${s}s`;
}
const RELATIVE_UNITS = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
];
export function formatRelativeTime(date, now = new Date()) {
    const target = typeof date === 'string' ? new Date(date) : date;
    const diffSeconds = (target.getTime() - now.getTime()) / 1000;
    const abs = Math.abs(diffSeconds);
    if (abs < 30)
        return 'agora';
    const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
    for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
        if (abs >= secondsInUnit || unit === 'second') {
            return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
        }
    }
    return 'agora';
}
/** Rótulo de data usado nos separadores de dia da lista de mensagens. */
export function formatDayLabel(date) {
    const target = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(now) - startOfDay(target)) / 86400000);
    if (diffDays === 0)
        return 'Hoje';
    if (diffDays === 1)
        return 'Ontem';
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: target.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    }).format(target);
}
export function formatTime(date) {
    const target = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(target);
}
export function formatNumber(value) {
    return new Intl.NumberFormat('pt-BR').format(value);
}
/** Extrai o timestamp embutido num snowflake. */
export const NEXUS_EPOCH = 1_700_000_000_000;
export function snowflakeToDate(id) {
    try {
        return new Date(Number(BigInt(id) >> 22n) + NEXUS_EPOCH);
    }
    catch {
        return new Date(0);
    }
}
/** Duas mensagens do mesmo autor em até 5 minutos são agrupadas visualmente. */
export const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000;
export function shouldGroupMessages(previous, current) {
    if (!previous)
        return false;
    if (previous.authorId !== current.authorId)
        return false;
    if (current.replyToId)
        return false;
    const delta = new Date(current.createdAt).getTime() - new Date(previous.createdAt).getTime();
    return delta >= 0 && delta < MESSAGE_GROUP_WINDOW_MS;
}
const MENTION_RE = /<@(\d+)>/g;
export function extractMentions(content) {
    const ids = new Set();
    for (const match of content.matchAll(MENTION_RE)) {
        if (match[1])
            ids.add(match[1]);
    }
    return [...ids];
}
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Backoff exponencial com jitter, usado na reconexão do gateway. */
export function backoffDelay(attempt, baseMs = 1000, maxMs = 30_000) {
    const exponential = Math.min(maxMs, baseMs * 2 ** attempt);
    return Math.floor(exponential * (0.5 + Math.random() * 0.5));
}
//# sourceMappingURL=utils.js.map